import React, { useMemo, useState } from "react";

import { Translate } from "@/app/application/Translate";
import { NewTrainingDrill } from "@/features/training/pages/drill-tab/panel-left/actions/NewTrainingDrill";
import { NewTrainingFolder } from "@/features/training/pages/training-tab/panel-left/actions/NewTrainingFolder";
import { CustomActionsButton } from "@/features/training/ui";
import { StyledMenu } from "@/shared/ui/StyledMenu";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import EngineeringIcon from "@mui/icons-material/Engineering";
import FestivalIcon from "@mui/icons-material/Festival";
import {
	Box,
	Divider,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Tooltip,
} from "@mui/material";

export default function TrainingDrillActions() {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [folderModal, setFolderModal] = useState(false);
	const [exerciseModal, setExerciseModal] = useState(false);
	const [eventModal, setEventModal] = useState(false);

	const open = Boolean(anchorEl);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => setAnchorEl(null);

	const openFolder = () => {
		setFolderModal(true);
		handleClose();
	};
	const openExercise = () => {
		setExerciseModal(true);
		handleClose();
	};
	const openEvent = () => {
		setEventModal(true);
		handleClose();
	};

	// Compact menu styling
	const paperSx = useMemo(
		() => ({
			borderRadius: 3,
			mt: 1,
			minWidth: 240,
			overflow: "hidden",
			border: "1px solid",
			borderColor: "divider",
			boxShadow:
				"0px 14px 30px rgba(0,0,0,0.12), 0px 6px 12px rgba(0,0,0,0.08)",
		}),
		[],
	);

	const itemSx = useMemo(
		() => ({
			mx: 0.75,
			my: 0.25,
			borderRadius: 2,
			px: 1,
			py: 0.75,
			minHeight: 38,
			"& .MuiListItemIcon-root": {
				minWidth: 30,
				color: "text.primary", // monochrome
			},
			"& .MuiListItemText-primary": {
				fontSize: 14.5,
				fontWeight: 500,
				letterSpacing: 0.1,
			},
			"&:hover": { backgroundColor: "action.hover" },
		}),
		[],
	);

	return (
		<>
			<Tooltip
				title={
					<Translate String="Training.Drills.Actions.Tooltip">
						Create a new folder, exercise, or event
					</Translate>
				}
				enterDelay={600}
			>
				<Box display="inline-flex">
					<CustomActionsButton
						open={open}
						handleClick={handleClick}
						aria-haspopup="menu"
						aria-controls={open ? "training-drill-actions-menu" : undefined}
						aria-expanded={open ? "true" : undefined}
					>
						<Translate String="Training.Actions">Actions</Translate>
					</CustomActionsButton>
				</Box>
			</Tooltip>

			<StyledMenu
				id="training-drill-actions-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				keepMounted
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				PaperProps={{ sx: paperSx }}
				MenuListProps={{
					"aria-labelledby": "training-drill-actions-button",
					dense: false,
					sx: { py: 0.5 },
				}}
			>
				<MenuItem sx={itemSx} onClick={openFolder}>
					<ListItemIcon>
						<CreateNewFolderIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Training.Drills.Actions.NewFolder"
							>
								New Folder
							</Translate>
						}
					/>
				</MenuItem>

				<MenuItem sx={itemSx} onClick={openExercise}>
					<ListItemIcon>
						<EngineeringIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Training.Drills.Actions.NewExercise"
							>
								New Exercise
							</Translate>
						}
					/>
				</MenuItem>

				<Divider sx={{ my: 0.5, mx: 1.5 }} />

				<MenuItem sx={itemSx} onClick={openEvent}>
					<ListItemIcon>
						<FestivalIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Training.Drills.Actions.NewEvent"
							>
								New Event
							</Translate>
						}
					/>
				</MenuItem>
			</StyledMenu>

			{/* Modals */}
			<NewTrainingFolder
				open={folderModal}
				handleClose={() => setFolderModal(false)}
			/>
			<NewTrainingDrill
				open={exerciseModal}
				handleClose={() => setExerciseModal(false)}
				type="Exercise"
			/>
			<NewTrainingDrill
				open={eventModal}
				handleClose={() => setEventModal(false)}
				type="Event"
			/>
		</>
	);
}
